import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Seo } from '../components/Seo'
import { useAuth } from '../auth/AuthProvider'
import { formatBytes } from '../lib/format'

interface Quota {
  quota_bytes: number
  used_bytes: number
  pending_bytes?: number
  remaining_bytes: number
}

type QuotaState =
  | { status: 'loading' }
  | { status: 'ok'; data: Quota }
  | { status: 'insufficient-scope' }
  | { status: 'error' }

interface Project {
  id: string
  name: string | null
  entry_path: string
  total_bytes: number
  file_count: number
  created_at: number
}

function basename(path: string): string {
  return path.split('/').pop() || path
}

function QuotaBar({ state }: { state: QuotaState }) {
  const { t } = useTranslation()
  if (state.status === 'loading') return null

  if (state.status === 'insufficient-scope') {
    return (
      <div className="card quota-card">
        <h2>{t('records.quota')}</h2>
        <p>{t('auth.reauthDesc')}</p>
        <a href="/api/auth/login">{t('auth.reauth')}</a>
      </div>
    )
  }
  if (state.status === 'error') return null

  const { data } = state
  const usedPct = data.quota_bytes > 0 ? Math.min(100, (data.used_bytes / data.quota_bytes) * 100) : 0
  const pending = data.pending_bytes && data.pending_bytes > 0

  return (
    <div className="card quota-card">
      <div className="quota-head">
        <h2>{t('records.quota')}</h2>
        <span className="num">
          {t('records.quotaUsed', {
            used: formatBytes(data.used_bytes),
            total: formatBytes(data.quota_bytes),
          })}
          {pending ? ` · ${t('records.pending', { size: formatBytes(data.pending_bytes!) })}` : ''}
        </span>
      </div>
      <div className="bar" role="progressbar" aria-valuenow={Math.round(usedPct)} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${usedPct}%` }} />
      </div>
      <span className="num">{t('records.quotaRemaining', { size: formatBytes(data.remaining_bytes) })}</span>
    </div>
  )
}

function ProjectList() {
  const { t, i18n } = useTranslation()
  const [projects, setProjects] = useState<Project[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copyLink = useCallback(async (p: Project) => {
    const url = `${window.location.origin}/p/${p.id}/`
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // 剪贴板被拒绝（如非安全上下文）时退回到老接口
      const ta = document.createElement('textarea')
      ta.value = url
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      ta.remove()
    }
    setCopiedId(p.id)
    window.setTimeout(() => setCopiedId((cur) => (cur === p.id ? null : cur)), 2000)
  }, [])

  useEffect(() => {
    let disposed = false
    void (async () => {
      try {
        const res = await fetch('/api/projects', { credentials: 'same-origin' })
        if (!res.ok) throw new Error(String(res.status))
        const data = (await res.json()) as { projects: Project[] }
        if (!disposed) setProjects(data.projects ?? [])
      } catch {
        if (!disposed) setLoadError(true)
      }
    })()
    return () => {
      disposed = true
    }
  }, [])

  const locale = i18n.language.startsWith('zh') ? 'zh-CN' : 'en-US'
  const fmtTime = (ts: number) =>
    new Date(ts).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  if (projects === null && !loadError) return <div className="spinner" aria-label="loading" />

  if (loadError) {
    return (
      <div className="card empty-state">
        <p className="error-text">{t('records.loadFailed')}</p>
      </div>
    )
  }

  if (!projects!.length) {
    return (
      <div className="card empty-state">
        <div className="icon" aria-hidden="true">🗂️</div>
        <h3>{t('records.empty')}</h3>
        <p>{t('records.emptyHint')}</p>
        <Link className="btn primary" to="/">
          {t('records.goUpload')}
        </Link>
      </div>
    )
  }

  return (
    <div>
      {projects!.map((p) => (
        <div className="card record-item" key={p.id}>
          <div className="record-head">
            <span className="record-title">{p.name || basename(p.entry_path)}</span>
            <div className="record-actions">
              <Link className="btn ghost sm" to={`/update/${p.id}`}>
                {t('records.update')}
              </Link>
              <a className="btn primary sm" href={`/p/${p.id}/`} target="_blank" rel="noreferrer">
                {t('records.preview')}
              </a>
              <button type="button" className="btn ghost sm" onClick={() => void copyLink(p)}>
                {copiedId === p.id ? t('records.copied') : t('records.copyLink')}
              </button>
            </div>
          </div>
          <div className="record-meta">
            <span>
              {t('records.entry')}: <code>{p.entry_path}</code>
            </span>
            <span>{t('records.files', { count: p.file_count })}</span>
            <span>{formatBytes(p.total_bytes)}</span>
            <span>{fmtTime(p.created_at)}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

export function Records() {
  const { t } = useTranslation()
  const { user, loading, login } = useAuth()
  const [quota, setQuota] = useState<QuotaState>({ status: 'loading' })

  const loadQuota = useCallback(async () => {
    setQuota({ status: 'loading' })
    try {
      const res = await fetch('/api/storage/quota', { credentials: 'same-origin' })
      if (res.status === 403) {
        setQuota({ status: 'insufficient-scope' })
        return
      }
      if (res.status === 401) {
        setQuota({ status: 'error' })
        return
      }
      const data = (await res.json()) as Quota
      setQuota({ status: 'ok', data })
    } catch {
      setQuota({ status: 'error' })
    }
  }, [])

  useEffect(() => {
    if (user) void loadQuota()
  }, [user, loadQuota])

  return (
    <main className="page container">
      <Seo title={t('seo.recordsTitle')} description={t('seo.recordsDesc')} path="/records" noIndex />

      <div className="page-head">
        <h1 className="page-title">{t('records.title')}</h1>
        <Link className="btn primary" to="/">
          {t('records.uploadNew')}
        </Link>
      </div>

      {loading ? (
        <div className="spinner" aria-label="loading" />
      ) : !user ? (
        <div className="card login-card">
          <div className="icon" aria-hidden="true">🔐</div>
          <h3>{t('auth.loginRequiredTitle')}</h3>
          <p>{t('auth.loginRequiredDesc')}</p>
          <button type="button" className="btn primary" onClick={login}>
            {t('auth.login')}
          </button>
        </div>
      ) : (
        <>
          <QuotaBar state={quota} />
          <ProjectList />
        </>
      )}
    </main>
  )
}
